import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SideNavigationContainer, SideNavigationItem, SideNavigationLink } from '@vendasta/atlas';
import { Observable, firstValueFrom } from 'rxjs';
import { BusinessNavConfigService, Config } from '../../config.service';

@Component({
  selector: 'atlas-nav-item-container',
  templateUrl: './nav-item-container.component.html',
  styleUrls: ['./nav-item-container.component.scss'],
  animations: [
    trigger('openClose', [
      state(
        'closed',
        style({
          height: '0px',
          opacity: 0,
          visibility: 'hidden',
          overflow: 'clip',
        }),
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: 1,
          visibility: 'visible',
          overflow: 'visible',
        }),
      ),
      transition('closed <=> open', [animate('0.2s')]),
    ]),
  ],
})
export class NavItemContainerComponent implements OnInit {
  @Input() indentation: number;
  @Input({ required: true }) set item(item: SideNavigationContainer) {
    this.children = item.sideNavigationItems;
    this._container = item;
  }
  protected _container: SideNavigationContainer;
  protected children: SideNavigationItem[];

  public text$: Observable<string>;
  public open = signal<boolean>(false);

  constructor(
    private t: TranslateService,
    private router: Router,
    private configService: BusinessNavConfigService,
  ) {}

  ngOnInit(): void {
    this.text$ = this.t.stream(this._container.translationId || this._container.label);
    firstValueFrom(this.configService.config$).then((config) => {
      if (this.checkChildrenForActiveRoute(this.children, config)) {
        this.open.set(true);
      }
    });
  }

  public toggle(): void {
    this.open.set(!this.open());
  }

  private checkChildrenForActiveRoute(children: SideNavigationItem[], config: Config): boolean {
    let active = false;
    if (!children || children.length === 0) {
      return active;
    }

    const curPath = `/${this.router.parseUrl(this.router.url).root.children.primary.toString()}`;

    for (const child of children) {
      if (child.sideNavigationSection) {
        active = this.checkChildrenForActiveRoute(child.sideNavigationSection.sideNavigationItems, config);
      } else if (child.sideNavigationContainer) {
        active = this.checkChildrenForActiveRoute(child.sideNavigationContainer.sideNavigationItems, config);
      } else if (child.sideNavigationLink.serviceProviderId === config.serviceProviderId) {
        active =
          curPath === child.sideNavigationLink.path ||
          this.checkSublinksForActiveRoute(child.sideNavigationLink.subLinks, config);
      }

      if (active) {
        break;
      }
    }

    return active;
  }

  private checkSublinksForActiveRoute(sublinks: SideNavigationLink[], config: Config): boolean {
    if (!sublinks || sublinks.length === 0) {
      return false;
    }

    const curPath = `/${this.router.parseUrl(this.router.url).root.children.primary.toString()}`;

    return sublinks.some(
      (sublink) => sublink.serviceProviderId === config.serviceProviderId && curPath === sublink.path,
    );
  }
}
